import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Chip, Button, Stack, ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  LocalBar as BarIcon, Restaurant as KitchenIcon, FiberManualRecord as DotIcon,
} from '@mui/icons-material';
import { ordersApi } from '../api/orders.api';
import { getSocket } from '../api/socket';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from '../components/common/LoadingSpinner';

type Mode = 'bar' | 'cocina';

const STATUS_META: Record<string, { label: string; color: string }> = {
  pendiente: { label: 'NUEVO', color: '#EF4444' },
  preparando: { label: 'EN PREPARACION', color: '#F59E0B' },
  listo: { label: 'LISTO', color: '#10B981' },
  servido: { label: 'SERVIDO', color: '#9CA3AF' },
};

// Siguiente accion segun el estado actual del item.
const NEXT: Record<string, { to: string; label: string; color: 'warning' | 'success' | 'inherit' }> = {
  pendiente: { to: 'preparando', label: 'Empezar a preparar', color: 'warning' },
  preparando: { to: 'listo', label: 'Marcar LISTO', color: 'success' },
  listo: { to: 'servido', label: 'Servido', color: 'inherit' },
};

const EstacionLive: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const roleMode: Mode = user?.role === 'cocinero' ? 'cocina' : 'bar';
  const [mode, setMode] = useState<Mode>(isAdmin ? 'cocina' : roleMode);
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async () => {
    try {
      const res = mode === 'bar' ? await ordersApi.getBar() : await ordersApi.getKitchen();
      setOrders(res.data || []);
    } catch (err) {
      console.error('Error cargando estacion:', err);
    } finally {
      setLoading(false);
    }
  }, [mode]);

  useEffect(() => {
    setLoading(true);
    load();
    // Tiempo real
    const socket = getSocket();
    if (!socket.connected) socket.connect();
    const handler = () => load();
    socket.on('orders:changed', handler);
    // Respaldo por si se cae el socket
    pollRef.current = setInterval(load, 10000);
    return () => {
      socket.off('orders:changed', handler);
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [load]);

  const advance = async (itemId: number, to: string) => {
    try {
      await ordersApi.updateItemStatus(itemId, to);
      load();
    } catch (err) {
      console.error('Error cambiando estado:', err);
    }
  };

  const accent = mode === 'bar' ? '#0EA5E9' : '#DC2626';

  if (loading && orders.length === 0) return <LoadingSpinner />;

  const totalItems = orders.reduce((n, o) => n + (o.items?.length || 0), 0);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {mode === 'bar' ? <BarIcon sx={{ fontSize: 32, color: accent }} /> : <KitchenIcon sx={{ fontSize: 32, color: accent }} />}
          <Typography variant="h4">{mode === 'bar' ? 'Bar' : 'Cocina'} en vivo</Typography>
          <Chip label={`${totalItems} item(s)`} size="small" sx={{ ml: 1 }} />
        </Box>
        {isAdmin && (
          <ToggleButtonGroup
            value={mode}
            exclusive
            size="small"
            onChange={(_e, v) => v && setMode(v)}
          >
            <ToggleButton value="cocina"><KitchenIcon sx={{ mr: 0.5, fontSize: 18 }} /> Cocina</ToggleButton>
            <ToggleButton value="bar"><BarIcon sx={{ mr: 0.5, fontSize: 18 }} /> Bar</ToggleButton>
          </ToggleButtonGroup>
        )}
      </Box>

      <Typography variant="body2" sx={{ mb: 2, color: '#6B7280' }}>
        Pedidos en tiempo real. Toca el boton de cada {mode === 'bar' ? 'bebida' : 'plato'} para cambiar su estado.
      </Typography>

      {orders.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8, color: '#9CA3AF' }}>
          <DotIcon sx={{ fontSize: 48, color: '#10B981' }} />
          <Typography variant="h6" sx={{ mt: 1 }}>Sin pendientes</Typography>
          <Typography variant="body2">No hay {mode === 'bar' ? 'bebidas' : 'platos'} en cola.</Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {orders.map((order) => (
            <Grid key={order.id} size={{ xs: 12, sm: 6, md: 4 }}>
              <Card sx={{ borderTop: `4px solid ${accent}` }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>{order.code}</Typography>
                    <Chip label={`Mesa ${order.table?.number ?? order.tableId}`} size="small" />
                  </Box>
                  {order.customerName && (
                    <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Cliente: {order.customerName}</Typography>
                  )}
                  {order.notes && (
                    <Typography variant="caption" sx={{ display: 'block', mb: 1, color: '#92400E', fontStyle: 'italic' }}>
                      Nota: {order.notes}
                    </Typography>
                  )}
                  <Stack spacing={1.5} sx={{ mt: 1 }}>
                    {(order.items || []).map((it: any) => {
                      const meta = STATUS_META[it.status] || { label: it.status, color: '#9CA3AF' };
                      const next = NEXT[it.status];
                      return (
                        <Box key={it.id} sx={{ borderLeft: `4px solid ${meta.color}`, pl: 1.5, py: 0.5 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                            <Typography sx={{ fontWeight: 600 }}>
                              {it.quantity}x {it.recipe?.name ?? `Item #${it.recipeId}`}
                            </Typography>
                            <Chip label={meta.label} size="small" sx={{ bgcolor: meta.color, color: '#fff', fontWeight: 700 }} />
                          </Box>
                          {it.notes && (
                            <Typography variant="caption" sx={{ color: '#92400E' }}>{it.notes}</Typography>
                          )}
                          {next && (
                            <Button
                              fullWidth
                              size="small"
                              variant="contained"
                              color={next.color === 'inherit' ? undefined : next.color}
                              sx={{ mt: 0.5, ...(next.color === 'inherit' ? { bgcolor: '#6366F1', '&:hover': { bgcolor: '#4F46E5' } } : {}) }}
                              onClick={() => advance(it.id, next.to)}
                            >
                              {next.label}
                            </Button>
                          )}
                        </Box>
                      );
                    })}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default EstacionLive;
